// Ingestion status — the diagnostic surface for "where is my roast?".
//
// Three things the backend reports and this page used to drop:
//   * stale_detector_records — records below the current detector version
//     (issue #7). Staleness is invisible unless something shows it.
//   * current_detector_version — what "stale" is relative to.
//   * sync — the uploader's health on this machine (issue #34). Null on
//     the cloud backend or when the state DB is not readable from here.

import { useEffect, useState } from "react";
import { useApi } from "../useApi";
import type { IngestionStatus, SyncHealth } from "../types";
import { ErrorBox } from "../components/ErrorBox";

export function Ingestion() {
  const api = useApi();
  const [status, setStatus] = useState<IngestionStatus | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    setErr(null);
    api
      .getIngestionStatus()
      .then(setStatus)
      .catch(setErr)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // Poll lightly while the user is looking at this page. 30s is enough for
    // a fresh file to be picked up by the watcher + Eventarc without hammering
    // the backend with list queries.
    const id = window.setInterval(refresh, 30_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stale = status?.stale_detector_records;

  return (
    <div className="col">
      <div className="row">
        <h1 style={{ margin: 0 }}>Ingestion</h1>
        <div className="right">
          <button onClick={refresh} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>
      <ErrorBox error={err} />
      {status?.summary && <p className="muted small" style={{ margin: 0 }}>{status.summary}</p>}

      <div className="card row" style={{ gap: 12, alignItems: "stretch" }}>
        <div className="stat">
          <div className="label">Last ingested</div>
          <div className="value" style={{ fontSize: "1em" }}>
            {status?.last_ingested_at ?? "–"}
          </div>
        </div>
        <div className="stat">
          <div className="label">Recent events</div>
          <div className="value">
            {status ? status.recent_events.length : "–"}
          </div>
          <div className="small muted">
            {status &&
              Object.entries(status.counts)
                .map(([k, v]) => `${k} ${v}`)
                .join(" · ")}
          </div>
        </div>
        <div className="stat">
          <div className="label">Detector version</div>
          <div className="value">
            {status ? `v${status.current_detector_version}` : "–"}
          </div>
        </div>
        <div className="stat">
          <div className="label">Stale records</div>
          <div className="value">
            {status == null ? "–" : stale == null ? "?" : stale}
          </div>
          <div className="small muted">
            {status == null
              ? ""
              : stale == null
                ? "count could not be taken"
                : stale === 0
                  ? "every record is on the current detector"
                  : `below v${status.current_detector_version} — run roast reprocess`}
          </div>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Sync</h2>
        {status == null ? (
          <p className="muted small">Loading…</p>
        ) : (
          <SyncBlock sync={status.sync} />
        )}
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Recent events</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Status</th>
                <th>Source</th>
                <th>Roast id</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {(status?.recent_events ?? []).map((e, i) => (
                <tr key={e.event_id ?? `${e.timestamp}-${i}`}>
                  <td className="mono small">{e.timestamp ?? "–"}</td>
                  <td>
                    <span className={`badge ${statusClass(e.status)}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="mono small">{e.source_file ?? "–"}</td>
                  <td className="mono small">{e.roast_id ?? "–"}</td>
                  <td className="small">{e.reason ?? ""}</td>
                </tr>
              ))}
              {!loading && (status?.recent_events?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ textAlign: "center" }}>
                    No recent ingestion events.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SyncBlock({ sync }: { sync: SyncHealth | null }) {
  if (sync == null) {
    return (
      <p className="muted small" style={{ margin: 0 }}>
        No sync health record readable from this backend. On the cloud
        service that is expected — the uploader's state lives on the
        roasting machine. Locally it means `roast sync` has never run here.
      </p>
    );
  }
  const state: { cls: "ok" | "warn" | "err"; label: string } = sync.failing
    ? { cls: "err", label: "failing" }
    : sync.stale
      ? { cls: "warn", label: "stale" }
      : { cls: "ok", label: "healthy" };
  return (
    <div className="col" style={{ gap: 6 }}>
      <div className="row">
        <span className={`badge ${state.cls}`}>{state.label}</span>
        <span className="small">
          {sync.hours_since_success == null
            ? "No successful sync recorded on this machine."
            : `Last successful sync ${sync.hours_since_success.toFixed(1)} h ago (${sync.last_success_at}).`}
        </span>
      </div>
      {sync.failing && (
        <p className="small" style={{ margin: 0 }}>
          Failing since <span className="mono">{sync.failing_since}</span>
          {sync.last_error_category && (
            <>
              {" "}· <span className="badge err">{sync.last_error_category}</span>
            </>
          )}
          {sync.last_error && <div className="muted mono small">{sync.last_error}</div>}
        </p>
      )}
      {!sync.failing && sync.last_failure_at && (
        <p className="small muted" style={{ margin: 0 }}>
          Last failure {sync.last_failure_at}
          {sync.last_error_category && ` (${sync.last_error_category})`} — recovered since.
        </p>
      )}
    </div>
  );
}

function statusClass(s: string | null | undefined): "ok" | "warn" | "err" | "" {
  if (s === "ok") return "ok";
  if (s === "skipped") return "warn";
  if (s === "failed") return "err";
  return "";
}
